import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button, Grid,
  FormControl, Select, MenuItem, InputLabel, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserDoc,
  getTenants,
  getAdminTenantByEmail,
  getUsersByTenant,
  addOrUpdateUser,
  deleteUser
} from '../db/userApi';
import { getFacilitiesByTenant } from '../db/facilityApi';
import { useLocation } from 'react-router-dom';

const UserManagement = () => {
  const { isSuperAdmin, isAppAdmin, user, isFacilityAdmin } = useAuth();
  const [adminTenantInfo, setAdminTenantInfo] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [userFormData, setUserFormData] = useState({
    userId: '', userName: '', userGroup: '', userEmailId: '', userMobileNo: '', role: 'visitor', tenantId: ''
  });
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const location = useLocation();
  const facilityFromNavigation = location.state?.facilityId || null;
  const queryParams = new URLSearchParams(location.search);
  const viewFacilityId = queryParams.get('viewFacilityId');
  const activeFacilityId =
    isFacilityAdmin()
      ? user?.facilityId
      : facilityFromNavigation || viewFacilityId || selectedFacilityId;

  useEffect(() => {
    if (isSuperAdmin()) fetchUsers();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!activeFacilityId) return;
    fetchUsers();
  }, [activeFacilityId]);

  useEffect(() => {
    const fetchTenantInfo = async () => {
      if (!user || isSuperAdmin()) return;
      try {
        const userDoc = await getUserDoc(user.uid);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.role === 'host') {
            setAdminTenantInfo({
              tenantId: userData.userID || user.uid,
              ...userData
            });
          }

          else if (userData.role === 'appadmin') {
            const userEmail =
              userData.userEmailID || userData.email || user.email;

            const tenantInfo = await getAdminTenantByEmail(userEmail);

            if (tenantInfo) {
              setAdminTenantInfo(tenantInfo);
            }
          }

          else if (userData.role === 'facilityadmin') {
            setAdminTenantInfo({
              tenantId: userData.tenantId,
              ...userData
            });
          }
        }
      } catch (error) {
        console.error("Error fetching admin tenant info:", error);
      }
    };
    fetchTenantInfo();
  }, [user, isSuperAdmin]);

  useEffect(() => {
    const loadFacilitiesAndUsers = async () => {
      if (!adminTenantInfo?.tenantId) return;

      const facilityData = await getFacilitiesByTenant(adminTenantInfo.tenantId);
      setFacilities(facilityData);

      // Auto-select first facility for AppAdmin
      if (isAppAdmin() && facilityData.length > 0) {
        setSelectedFacilityId(facilityData[0].id);
      }

      fetchUsers();
    };

    loadFacilitiesAndUsers();
  }, [adminTenantInfo]);

  useEffect(() => {
    const fetchAvailableTenants = async () => {
      if (!isSuperAdmin() && !isAppAdmin()) return;
      try {
        const tenants = await getTenants();
        setAvailableTenants(tenants);
        const storedTenantId = sessionStorage.getItem('selectedTenantId');
        if (storedTenantId) {
          setSelectedTenantId(storedTenantId);
          const selectedTenant = tenants.find(t => t.tenantId === storedTenantId);
          if (selectedTenant) setAdminTenantInfo(selectedTenant);
        }
      } catch (error) {
        console.error("Error loading tenants:", error);
      }
    };
    fetchAvailableTenants();
  }, [isSuperAdmin, isAppAdmin]);

  const handleTenantChange = (e) => {
    const tenantId = e.target.value;
    setSelectedTenantId(tenantId);
    sessionStorage.setItem('selectedTenantId', tenantId);
    const selectedTenant = availableTenants.find(t => t.tenantId === tenantId);
    if (selectedTenant) setAdminTenantInfo(selectedTenant);
  };

  const fetchUsers = async () => {
    try {
      const tenantId = adminTenantInfo?.tenantId;
      if (!tenantId) return;
      const data = await getUsersByTenant(tenantId);
      let filteredUsers = data;
      if (isAppAdmin()) {
        if (!activeFacilityId) {
          setUsers([]);
          return;
        }
        filteredUsers = data.filter(
          (u) => u.facilityId === activeFacilityId
        );
      }
      if (isFacilityAdmin()) {
        filteredUsers = data.filter(
          (u) =>
            u.facilityId === activeFacilityId &&
            u.role !== 'facilityadmin'
        );
      }
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setErrors({ submission: "Failed to fetch users." });
    }
  };

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'role' && value !== 'appoperator') {
      setUserFormData(prev => ({ ...prev, tenantId: '' }));
    }
  };

  const validateUserForm = () => {
    let tempErrors = {};
    let valid = true;
    if (!userFormData.userName) {
      tempErrors.userName = 'User Name is required';
      valid = false;
    }
    if (!userFormData.userEmailId) {
      tempErrors.userEmailId = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(userFormData.userEmailId)) {
      tempErrors.userEmailId = 'Invalid email format';
      valid = false;
    }
    setErrors(tempErrors);
    return valid;
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!validateUserForm()) return;
    setIsSubmitting(true);
    try {
      if (isAppAdmin() && userFormData.role === 'facilityadmin' && !selectedFacilityId) {
        setErrors({ submission: 'Please select a facility before adding a Facility Admin.' });
        setIsSubmitting(false);
        return;
      }
      const tenantIdToUse = isFacilityAdmin()
        ? user?.tenantId
        : adminTenantInfo?.tenantId;

      if (!tenantIdToUse) {
        setErrors({ submission: "Tenant not found. Please refresh." });
        setIsSubmitting(false);
        return;
      }

      const finalFacilityId = activeFacilityId || '';

      const cleanedUserData = {
        ...userFormData,
        tenantId: tenantIdToUse,
        facilityId: finalFacilityId || ''
      };

      // Remove undefined fields (VERY IMPORTANT)
      Object.keys(cleanedUserData).forEach(
        key => cleanedUserData[key] === undefined && delete cleanedUserData[key]
      );

      await addOrUpdateUser(
        cleanedUserData,
        tenantIdToUse,
        selectedItem,
        // import.meta.env.VITE_FIREBASE_API_KEY
      );

      setUserFormData({ userId: '', userName: '', userGroup: '', userEmailId: '', userMobileNo: '', role: 'visitor', tenantId: '' });
      setSelectedItem(null);
      if (selectedItem) {
        setSuccessMessage('User updated successfully!');
      } else {
        setSuccessMessage('User added successfully!');
      }
      fetchUsers();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error("User add/update error:", error);
      setErrors({ submission: "Operation failed. Try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setUserFormData({
      userId: item.userId,
      userName: item.userName,
      userGroup: item.userGroup,
      userEmailId: item.userEmailId,
      userMobileNo: item.userMobileNo,
      role: item.role,
      tenantId: item.tenantId || ''
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        setIsSubmitting(true);
        await deleteUser(id);
        setUsers(prev => prev.filter(user => user.userId !== id));
        setSuccessMessage('User deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error) {
        console.error("Delete error:", error);
        setErrors({ submission: "Failed to delete user." });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (facilityFromNavigation) {
      setSelectedFacilityId(facilityFromNavigation);
    }
  }, [facilityFromNavigation]);

  if (user?.role === 'superadmin') {
    return (
      <Container sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography color="error">
            Super Admins are not allowed to manage users.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!(isAppAdmin() || isFacilityAdmin())) {
    return (
      <Container sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography color="error">
            Access Denied
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
  <Box sx={{ minHeight: "100vh", background: "#fefce8", py: 4 }}>
    <Container maxWidth="lg" sx={{ mt: 4 }}>

      {/* HEADER */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: "2.2rem",
          fontWeight: 900,
          fontFamily: "'Unbounded', sans-serif",
          color: "#1c1400"
        }}>
          User Management
        </Typography>

        <Typography sx={{ color: "#8a7a40" }}>
          Manage users, roles and access across your system
        </Typography>
      </Box>

      {/* ALERTS */}
      {successMessage && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: "12px",
          background: "#eaf7e9",
          border: "1px solid #22c55e"
        }}>
          {successMessage}
        </Box>
      )}

      {errors.submission && (
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: "12px",
          background: "#fee2e2",
          border: "1px solid #dc2626"
        }}>
          {errors.submission}
        </Box>
      )}

      {/* ================= FORM ================= */}
      <Paper sx={{
        p: 4,
        mb: 4,
        borderRadius: "16px",
        border: "1px solid #f0d060",
        background: "#fff"
      }}>

        <Typography sx={{ fontWeight: 700, mb: 2 }}>
          {selectedItem ? "Update User" : "Add User"}
        </Typography>

        <form onSubmit={handleUserSubmit}>
          <Grid container spacing={3}>

            <Grid item xs={12} sm={6}>
              <TextField
                name="userName"
                label="User Name"
                value={userFormData.userName}
                onChange={handleUserFormChange}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="userEmailId"
                label="Email"
                value={userFormData.userEmailId}
                onChange={handleUserFormChange}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="userMobileNo"
                label="Mobile"
                value={userFormData.userMobileNo}
                onChange={handleUserFormChange}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={userFormData.role}
                  onChange={handleUserFormChange}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="visitor">Visitor</MenuItem>
                  {(isAppAdmin() || isFacilityAdmin()) && <MenuItem value="appoperator">Operator</MenuItem>}
                  {isAppAdmin() && <MenuItem value="facilityadmin">Facility Admin</MenuItem>}
                  {(isAppAdmin() || isFacilityAdmin()) && <MenuItem value="host">Host</MenuItem>}
                </Select>
              </FormControl>
            </Grid>

            {/* BUTTONS */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    background: "#1c1400",
                    color: "#fde047",
                    fontWeight: 600,
                    px: 4,
                    "&:hover": {
                      background: "#f59e0b",
                      color: "#1c1400"
                    }
                  }}
                >
                  {selectedItem ? "Update User" : "Add User"}
                </Button>

                {selectedItem && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSelectedItem(null);
                      setUserFormData({
                        userId: '',
                        userName: '',
                        userGroup: '',
                        userEmailId: '',
                        userMobileNo: '',
                        role: 'visitor',
                        tenantId: ''
                      });
                    }}
                    sx={{
                      borderColor: "#1c1400",
                      color: "#1c1400",
                      "&:hover": {
                        borderColor: "#f59e0b",
                        color: "#f59e0b"
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}

              </Box>
            </Grid>

          </Grid>
        </form>
      </Paper>

      {/* ================= TABLE ================= */}
      <Paper sx={{
        p: 2,
        borderRadius: "16px",
        border: "1px solid #f0d060",
        background: "#ffffff"
      }}>

        <Typography sx={{ fontWeight: 700, mb: 2 }}>
          Users List
        </Typography>

        <Table>
          <TableHead>
            <TableRow sx={{ background: "#fef3c7" }}>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Email</b></TableCell>
              <TableCell><b>Role</b></TableCell>
              <TableCell align="center"><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.userId}
                sx={{
                  "&:hover": {
                    background: "#fffbea"
                  }
                }}
              >
                <TableCell>{user.userName}</TableCell>
                <TableCell>{user.userEmailId}</TableCell>
                <TableCell>{user.role}</TableCell>

                <TableCell align="center">
                  <IconButton onClick={() => handleEdit(user)}>
                    <Edit sx={{ color: "#f59e0b" }} />
                  </IconButton>

                  <IconButton onClick={() => handleDelete(user.userId)}>
                    <Delete sx={{ color: "#dc2626" }} />
                  </IconButton>
                </TableCell>

              </TableRow>
            ))}
          </TableBody>
        </Table>

      </Paper>

    </Container>
  </Box>
);
};

export default UserManagement;